import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';

// services
import { getNotifications, markAsRead, markAllAsRead, deleteNotification } from 'services/notificationService';
import { getSocket } from 'services/socket';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import CalendarOutlined from '@ant-design/icons/CalendarOutlined';
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import BankOutlined from '@ant-design/icons/BankOutlined';

// sx styles
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',
  transform: 'none'
};

// Helper to format "time ago"
const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
};

// Map notification type to Icon config
const getNotificationConfig = (type, priority) => {
  let icon = <BellOutlined />;
  let color = 'primary.main';
  let bgcolor = 'primary.lighter';

  switch (type) {
    case 'appointment_booked':
    case 'appointment_rescheduled':
      icon = <CalendarOutlined />;
      color = 'info.main';
      bgcolor = 'info.lighter';
      break;
    case 'appointment_confirmed':
    case 'appointment_completed':
      icon = <CheckCircleOutlined />;
      color = 'success.main';
      bgcolor = 'success.lighter';
      break;
    case 'appointment_cancelled':
    case 'appointment_rejected':
      icon = <CloseCircleOutlined />;
      color = 'error.main';
      bgcolor = 'error.lighter';
      break;
    case 'doctor_leave':
      icon = <WarningOutlined />;
      color = 'error.main';
      bgcolor = 'error.lighter';
      break;
    case 'reminder':
      icon = <ClockCircleOutlined />;
      color = 'warning.main';
      bgcolor = 'warning.lighter';
      break;
    case 'appointment_status_changed':
      icon = <InfoCircleOutlined />;
      color = 'primary.main';
      bgcolor = 'primary.lighter';
      break;
    case 'medical_record':
      icon = <FileTextOutlined />;
      color = 'secondary.main';
      bgcolor = 'secondary.lighter';
      break;
    case 'clinic_created':
      icon = <BankOutlined />;
      color = 'success.main';
      bgcolor = 'success.lighter';
      break;
    case 'clinic_status_changed':
      icon = <BankOutlined />;
      color = 'warning.main';
      bgcolor = 'warning.lighter';
      break;
    case 'subscription_requested':
      icon = <BankOutlined />;
      color = 'warning.main';
      bgcolor = 'warning.lighter';
      break;
    case 'subscription_approved':
    case 'subscription_payment_success':
      icon = <CheckCircleOutlined />;
      color = 'success.main';
      bgcolor = 'success.lighter';
      break;
    case 'subscription_payment_failed':
      icon = <CloseCircleOutlined />;
      color = 'error.main';
      bgcolor = 'error.lighter';
      break;
    case 'system':
    default:
      icon = <SettingOutlined />;
      color = 'secondary.main';
      bgcolor = 'secondary.lighter';
      break;
  }

  // Priority override for urgent status
  if (priority === 'urgent') {
    color = 'error.main';
    bgcolor = 'error.lighter';
  }

  return { icon, color, bgcolor };
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Initial load and WebSocket real-time listener for notifications & badge count
  useEffect(() => {
    fetchNotificationsList(1);
    const socket = getSocket();
    if (socket) {
      const handleNewNotification = (notification) => {
        setNotifications((prev) => [notification, ...prev]);
        setUnreadCount((prev) => prev + 1);
      };
      
      socket.on('NEW_NOTIFICATION', handleNewNotification);
      return () => {
        socket.off('NEW_NOTIFICATION', handleNewNotification);
      };
    }
  }, []);

  // Fetch paginated notifications list
  const fetchNotificationsList = async (loadPage = 1) => {
    try {
      setLoading(true);
      const res = await getNotifications({ page: loadPage, limit: 5 });
      if (res && res.notifications) {
        if (loadPage === 1) {
          setNotifications(res.notifications);
        } else {
          setNotifications((prev) => [...prev, ...res.notifications]);
        }
        setUnreadCount(res.unread_count || 0);
        setPage(loadPage);
        setHasMore(res.pagination ? loadPage < res.pagination.pages : false);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Listen to custom event to toggle notifications drawer from sidebar
  useEffect(() => {
    const handleToggleEvent = () => {
      setOpen((prevOpen) => !prevOpen);
    };
    window.addEventListener('toggle-notifications-drawer', handleToggleEvent);
    return () => window.removeEventListener('toggle-notifications-drawer', handleToggleEvent);
  }, []);

  // Fetch notifications list when dropdown is opened
  useEffect(() => {
    if (open) {
      fetchNotificationsList(1);
    }
  }, [open]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  // Action: Mark all notifications as read
  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Action: Mark single notification as read
  const handleMarkAsRead = async (id) => {
    try {
      await markAsRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  // Action: Soft delete notification
  const handleDelete = async (e, id, isRead) => {
    e.stopPropagation();
    try {
      await deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (!isRead) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Navigation click routing helper
  const handleNotificationClick = async (notif) => {
    if (!notif.is_read) {
      await handleMarkAsRead(notif.id);
    }
    setOpen(false);

    // Dynamic routing depending on metadata/entity
    const storedUser = JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user') || '{}');
    const roleLower = String(storedUser?.role_name || storedUser?.role?.role_name || storedUser?.role || '').toLowerCase();
    const isSuperAdmin = roleLower.includes('super') || storedUser?.role_id === 1;
    const isDoctor = roleLower.includes('doctor');
    const isPatient = roleLower.includes('patient');

    // 1. Appointment Notifications
    if (
      notif.entity_type === 'appointment' ||
      notif.type?.startsWith('appointment') ||
      notif.type === 'reminder'
    ) {
      if (notif.entity_id) {
        navigate(`/appointments/${notif.entity_id}`);
      } else if (isPatient || isDoctor) {
        navigate('/appointments/my');
      } else {
        navigate('/appointments/queue');
      }
      return;
    }

    // 2. Subscription Notifications
    if (
      notif.type === 'subscription_payment_success' ||
      notif.type === 'subscription_payment_failed' ||
      notif.type === 'subscription_requested' ||
      notif.type === 'subscription_approved' ||
      notif.entity_type === 'subscription_request'
    ) {
      if (isSuperAdmin) {
        navigate('/admin/clinics?tab=1');
      } else {
        navigate('/subscription');
      }
      return;
    }

    // 3. Clinic Management Notifications
    if (notif.entity_type === 'clinic' || notif.type === 'clinic_created' || notif.type === 'clinic_status_changed') {
      if (isSuperAdmin) {
        navigate('/admin/clinics');
      } else {
        navigate('/clinic/profile');
      }
      return;
    }

    // 4. Clinical & Lab Workflows
    if (notif.type === 'medical_record' || notif.entity_type === 'medical_record') {
      navigate('/clinical/medical-records');
      return;
    }
    if (notif.type === 'prescription' || notif.entity_type === 'prescription') {
      navigate('/clinical/prescriptions');
      return;
    }
    if (notif.type === 'lab_request' || notif.type === 'lab_test' || notif.entity_type === 'lab_request') {
      navigate('/clinical/lab-requests');
      return;
    }
    if (notif.type === 'external_lab_work' || notif.entity_type === 'external_lab_work') {
      navigate('/clinical/external-lab-work');
      return;
    }
    if (notif.type === 'medical_report') {
      navigate('/clinical/medical-reports');
      return;
    }

    // 5. Doctor Schedule, Leaves & Breaks
    if (notif.type === 'doctor_leave' || notif.entity_type === 'doctor_leave') {
      navigate('/doctor/leaves');
      return;
    }
    if (notif.type === 'doctor_break' || notif.entity_type === 'doctor_break') {
      navigate('/doctor/breaks');
      return;
    }
    if (notif.type === 'schedule_update') {
      navigate('/doctor/schedule');
      return;
    }

    // 6. Billing & Invoices
    if (notif.type === 'invoice' || notif.entity_type === 'invoice' || notif.type?.includes('billing')) {
      navigate('/billing/invoices');
      return;
    }

    // 7. Account created / welcome
    if (notif.type === 'account_created') {
      navigate(isPatient ? '/appointments/my' : isDoctor ? '/doctor/schedule' : '/dashboard/default');
      return;
    }

    // Fallback: Dashboard
    navigate('/dashboard/default');
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={(theme) => ({
          color: 'inherit',
          bgcolor: open ? 'rgba(255,255,255,0.1)' : 'transparent'
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={unreadCount} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 320, maxWidth: { xs: 320, md: 440 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notifications"
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    <>
                      {unreadCount > 0 && (
                        <Tooltip title="Mark all notifications as read">
                          <IconButton color="success" size="small" onClick={handleMarkAllAsRead}>
                            <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  }
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      maxHeight: 380,
                      overflowY: 'auto',
                      '& .MuiListItemButton-root': {
                        py: 1.25,
                        px: 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '& .MuiAvatar-root': avatarSX,
                        '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                      }
                    }}
                  >
                    {notifications.length === 0 && !loading ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <InfoCircleOutlined style={{ fontSize: '2rem', color: '#8c8c8c', marginBottom: 8 }} />
                        <Typography variant="subtitle2" color="text.secondary">
                          You&apos;re all caught up! No notifications.
                        </Typography>
                      </Box>
                    ) : (
                      notifications.map((notif) => {
                        const { icon, color, bgcolor } = getNotificationConfig(notif.type, notif.priority);
                        return (
                          <ListItem
                            key={notif.id}
                            component={ListItemButton}
                            onClick={() => handleNotificationClick(notif)}
                            sx={{
                              bgcolor: notif.is_read ? 'transparent' : 'action.hover',
                              borderLeft: notif.is_read ? 'none' : '3.5px solid',
                              borderLeftColor: color,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: 'action.selected'
                              }
                            }}
                            secondaryAction={
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {timeAgo(notif.created_at)}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  {!notif.is_read && (
                                    <Tooltip title="Mark as read">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleMarkAsRead(notif.id);
                                        }}
                                        sx={{ p: 0.25 }}
                                      >
                                        <CheckCircleOutlined style={{ fontSize: '0.85rem' }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={(e) => handleDelete(e, notif.id, notif.is_read)}
                                      sx={{ p: 0.25 }}
                                    >
                                      <DeleteOutlined style={{ fontSize: '0.85rem' }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            }
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ color: color, bgcolor: bgcolor }}>{icon}</Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography variant="subtitle2" sx={{ fontWeight: notif.is_read ? 500 : 700 }}>
                                  {notif.title}
                                </Typography>
                              }
                              secondary={
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, pr: 2 }}>
                                  {notif.message}
                                </Typography>
                              }
                            />
                          </ListItem>
                        );
                      })
                    )}

                    {loading && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    )}

                    {hasMore && !loading && (
                      <Box sx={{ p: 1, textAlign: 'center' }}>
                        <Button size="small" onClick={() => fetchNotificationsList(page + 1)} sx={{ textTransform: 'none' }}>
                          Load More
                        </Button>
                      </Box>
                    )}
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
